import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service, TemplateResolve } from 'webapp-types';
import { DATASET_GROUP_TYPE_MERGED } from 'webapp/const/template-resolve';
import { TemplateObjectType } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonAskToConnectButtonComponent from 'webapp/components/common/ask-to-connect-button';
import CommonSecurityInfoComponent from 'webapp/components/common/security-info';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ResolveDataItemChoiceComponent from 'webapp/components/resolve-data-item-choice';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TemplatesPopupResolveDatasetComponent from 'webapp/components/templates/popup-resolve-dataset';
import type { DatasetSelection } from 'webapp/components/templates/popup-resolve-dataset';

import ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import type { SourceData } from 'webapp/models-custom/resolve-sources-data';
import ResolveSourcesData from 'webapp/models-custom/resolve-sources-data';
import Template from 'webapp/models-custom/template';

export default class TemplatesPopupResolveTemplateComponent extends PopupInnerModalComponent<PopupData.Templates.ResolveTransferOrTemplate> {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	resolveSourcesData: ResolveSourcesData;
	objectType: TemplateObjectType;

	@tracked step = 1;
	@tracked resolutionInfo: APIData.Templates.ResolutionInfoResponse | null = null;
	@tracked resolutionInfoLoading = false;
	@tracked datasetGroups: TemplateResolve.DatasetGroupInfo[] = [];
	@tracked currentDatasetGroupIndex = 0;
	@tracked compatibleDatasets: TemplateResolve.CompatibleDatasetItem[] = [];
	@tracked compatibleDatasetsLoading = false;
	private compatibleDatasetsRequestId = 0;

	// collected dataset resolution state
	@tracked existingDatasetMap: Record<string, string> = {};
	@tracked skippedDatasetGroups: string[] = [];
	@tracked datasetSourceMap: Record<string, Record<string, string>> = {};
	@tracked datasetSelections: Record<string, DatasetSelection> = {};
	private scrollContainerElement: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.resolveSourcesData = new ResolveSourcesData(this.model);

		if (this.model instanceof ResolutionCenterItem) {
			this.objectType = this.model.isTransferedReportType
				? TemplateObjectType.REPORT
				: TemplateObjectType.BOARD;
		} else {
			this.objectType = this.model?.objectType;
		}

		void this.loadResolutionInfo();
	}

	get model() {
		return this.popupData.model;
	}

	get objectTypeString(): string {
		switch (this.objectType) {
			case TemplateObjectType.BOARD:
				return 'dashboard';
			case TemplateObjectType.REPORT:
				return 'report';
			case TemplateObjectType.BOARD_LOOP:
				return 'looped dashboard';
			default:
				return '';
		}
	}

	get isSecurityInfoShown() {
		const { sourcesToConnect, sourcesToChoose } = this.resolveSourcesData;
		return !!sourcesToConnect.length && !sourcesToChoose.length;
	}

	@action
	connect(sourceData: SourceData): void {
		void this.resolveSourcesData.connect(sourceData, 'modalResolvetransfer');
	}

	@action
	resolveSource(sourceData: SourceData, sourceId: string | null): void {
		const sourceTypeId = sourceData.sourceType.id;
		if (sourceTypeId) {
			this.resolveSourcesData.resolve(sourceTypeId, sourceId, true);
		}
	}

	get shouldShowDatasetSteps(): boolean {
		return this.datasetGroups.length > 0;
	}

	get isOnSourceStep(): boolean {
		return this.step === 1;
	}

	get sourceNoun(): string {
		return this.resolveSourcesData.hasMultipleSources ? 'data sources' : 'data source';
	}

	get currentDatasetGroup(): TemplateResolve.DatasetGroupInfo | null {
		if (this.currentDatasetGroupIndex < this.datasetGroups.length) {
			return this.datasetGroups[this.currentDatasetGroupIndex]!;
		}
		return null;
	}

	get currentDatasetInitialSelection(): DatasetSelection | undefined {
		const group = this.currentDatasetGroup;
		if (!group) return undefined;
		return this.datasetSelections[this.groupKey(group)];
	}

	get totalSteps(): number {
		return this.datasetGroups.length;
	}

	get hasRegularSources(): boolean {
		const model = this.model;
		if (model instanceof Template) {
			return model.sources.length > 0;
		}
		return (
			(model.params?.dataSourceTypeIds?.length ?? 0) > 0 || model.params?.hasDatasets === true
		);
	}

	get mergedDatasetGroups() {
		return this.datasetGroups
			.filter((g) => g.type === DATASET_GROUP_TYPE_MERGED)
			.map((g) => ({
				...g,
				requiredSources: this.resolveGroupSources(g),
			}));
	}

	private resolveGroupSources(group: TemplateResolve.DatasetGroupInfo) {
		const groupKey = String(group.templateSpaceAccessId);

		return (group.requiredSources || [])
			.filter((s) => s.dataSourceTypeId !== null && s.dataSourceTypeId !== undefined)
			.map((s) => {
				const sourceType = this.mmSourceTypesService.findModelById(s.dataSourceTypeId!);
				if (!sourceType) return null;
				return {
					...s,
					resolvedName: sourceType.name ?? s.sourceName ?? '',
					resolvedAvatar: sourceType.avatar,
					sourceData: {
						sourceType,
						connectedSourceId:
							this.datasetSourceMap[groupKey]?.[String(s.templateId)] || null,
					},
				};
			})
			.filter((item) => item !== null);
	}

	get allMergeSourcesResolved(): boolean {
		for (const group of this.mergedDatasetGroups) {
			const key = String(group.templateSpaceAccessId);
			const selections = this.datasetSourceMap[key];
			if (!selections) return false;
			for (const source of group.requiredSources || []) {
				if (!selections[String(source.templateId)]) return false;
			}
		}
		return true;
	}

	get isContinueButtonDisabled(): boolean {
		// Regular sources may be left unresolved (template resolve is allowed
		// without selecting any sources); only merged-dataset sources must be
		// resolved before continuing.
		return this.mergedDatasetGroups.length > 0 && !this.allMergeSourcesResolved;
	}

	get title(): string {
		if (!this.hasRegularSources && this.mergedDatasetGroups.length > 0) {
			return 'Connect your data';
		}

		const resolveSourcesData = this.resolveSourcesData;
		let title = '';

		if (resolveSourcesData.hasOnlyChoose) {
			title = 'Choose';
		} else if (resolveSourcesData.hasOnlyConnect) {
			title = 'Connect';
		} else {
			title = 'Connect / choose';
		}

		title += ` data source${resolveSourcesData.hasMultipleSources ? 's' : ''}`;

		return title;
	}

	@action
	captureScrollContainer(element: HTMLElement): void {
		this.scrollContainerElement = element;
	}

	@action
	resolveMergeSource(
		group: TemplateResolve.DatasetGroupInfo,
		source: TemplateResolve.DatasetGroupRequiredSource,
		_sourceData: SourceData,
		sourceId: string | null,
	): void {
		const scrollTop = this.scrollContainerElement?.scrollTop ?? 0;

		const groupKey = String(group.templateSpaceAccessId);
		const sourceKey = String(source.templateId);

		if (!this.datasetSourceMap[groupKey]) {
			this.datasetSourceMap[groupKey] = {};
		}

		if (sourceId) {
			this.datasetSourceMap[groupKey][sourceKey] = sourceId;
		} else {
			delete this.datasetSourceMap[groupKey][sourceKey];
		}

		this.datasetSourceMap = { ...this.datasetSourceMap };

		requestAnimationFrame(() => {
			if (this.scrollContainerElement) {
				this.scrollContainerElement.scrollTop = scrollTop;
			}
		});
	}

	private async loadResolutionInfo(): Promise<void> {
		const model = this.model;
		if (!model) return;

		let resolutionInfoUrl: string;
		if (model instanceof Template && model.id) {
			resolutionInfoUrl = `/templates/${model.id}/resolution-info`;
		} else if (model instanceof ResolutionCenterItem && model.boardIds?.[0]) {
			resolutionInfoUrl = `/boards/${model.boardIds[0]}/resolution-info`;
		} else {
			return;
		}

		this.resolutionInfoLoading = true;
		try {
			const info = await this.requestService.api<APIData.Templates.ResolutionInfoResponse>(
				'GET',
				resolutionInfoUrl,
			);
			this.resolutionInfo = info;
			this.datasetGroups = info.datasetGroups || [];
		} catch {
			this.datasetGroups = [];
		} finally {
			this.resolutionInfoLoading = false;
			this.scheduleReposition();
		}

		if (
			!(this.model instanceof ResolutionCenterItem) &&
			!this.hasRegularSources &&
			this.datasetGroups.length === 0
		) {
			this.completeResolution();
		}
	}

	private async loadCompatibleDatasets(): Promise<void> {
		const group = this.currentDatasetGroup;
		if (!group) {
			return;
		}

		// Increment counter so concurrent requests from rapid step navigation
		// can be detected — if a newer request starts before this one finishes,
		// this response is stale and should be discarded
		const requestId = ++this.compatibleDatasetsRequestId;
		this.compatibleDatasetsLoading = true;
		this.compatibleDatasets = [];

		try {
			if (!group.templateId) {
				this.compatibleDatasets = [];
				this.compatibleDatasetsLoading = false;
				return;
			}

			let parentSpaceAccessIds: string[];

			if (group.type === 'merged_dataset') {
				const mergeSelections =
					this.datasetSourceMap[String(group.templateSpaceAccessId)] || {};
				parentSpaceAccessIds = Object.values(mergeSelections);
			} else {
				parentSpaceAccessIds = Object.values(this.resolveSourcesData.sourcesMap);
			}

			const response =
				await this.requestService.api<APIData.Templates.CompatibleDatasetsResponse>(
					'POST',
					`/templates/compatible-datasets/${group.templateId}`,
					{ parentSpaceAccessIds },
				);

			if (requestId !== this.compatibleDatasetsRequestId) return;
			this.compatibleDatasets = response.datasets || [];
		} catch {
			if (requestId !== this.compatibleDatasetsRequestId) return;
			this.compatibleDatasets = [];
		} finally {
			if (requestId === this.compatibleDatasetsRequestId) {
				this.compatibleDatasetsLoading = false;
				this.scheduleReposition();
			}
		}
	}

	private groupKey(group: TemplateResolve.DatasetGroupInfo): string {
		return String(group.templateSpaceAccessId);
	}

	@action
	finishResolving(): void {
		if (this.shouldShowDatasetSteps) {
			this.step = 2;
			this.currentDatasetGroupIndex = 0;
			this.scheduleReposition();
			void this.loadCompatibleDatasets();
		} else {
			this.completeResolution();
		}
	}

	@action
	onDatasetContinue(selection: DatasetSelection): void {
		const group = this.currentDatasetGroup;
		if (!group) {
			return;
		}

		const key = this.groupKey(group);

		this.datasetSelections = { ...this.datasetSelections, [key]: selection };

		if (selection === 'create_new') {
			delete this.existingDatasetMap[key];
		} else {
			this.existingDatasetMap[key] = String(selection);
		}

		// remove from skipped if it was previously skipped
		this.skippedDatasetGroups = this.skippedDatasetGroups.filter((k) => k !== key);

		this.advanceDatasetStep();
	}

	@action
	onDatasetSkip(): void {
		const group = this.currentDatasetGroup;
		if (!group) {
			return;
		}

		const key = this.groupKey(group);
		delete this.existingDatasetMap[key];

		if (!this.skippedDatasetGroups.includes(key)) {
			this.skippedDatasetGroups = [...this.skippedDatasetGroups, key];
		}

		this.advanceDatasetStep();
	}

	@action
	onDatasetBack(currentSelection: DatasetSelection): void {
		const group = this.currentDatasetGroup;
		if (group) {
			const key = this.groupKey(group);
			this.datasetSelections = { ...this.datasetSelections, [key]: currentSelection };
		}

		if (this.currentDatasetGroupIndex > 0) {
			this.currentDatasetGroupIndex--;
			this.step--;
			void this.loadCompatibleDatasets();
		} else {
			this.step = 1;
		}

		this.scheduleReposition();
	}

	private advanceDatasetStep(): void {
		if (this.currentDatasetGroupIndex < this.datasetGroups.length - 1) {
			this.currentDatasetGroupIndex++;
			this.step++;
			this.scheduleReposition();
			void this.loadCompatibleDatasets();
		} else {
			this.completeResolution();
		}
	}

	private completeResolution(): void {
		const payload: TemplateResolve.NormalizedResolvePayload = {
			dataSourceTypeMap: this.resolveSourcesData.sourcesMap,
			...(this.shouldShowDatasetSteps && {
				datasetSourceMap: this.datasetSourceMap,
				existingDatasetMap: this.existingDatasetMap,
				skippedDatasetGroups: this.skippedDatasetGroups,
			}),
		};

		if (this.popupData.onAllResolved) {
			this.popupData.onAllResolved(payload);
		}

		this.close();
	}

	<template>
		{{#if this.isOnSourceStep}}
			{{! Step 1: Source selection }}
			<div class="title">
				<CommonUiIconComponent @name="Connect" @size="lg" />
				{{this.title}}
			</div>

			<div class="content">
				<p>
					You are just a step away from seeing the selected
					{{this.objectTypeString}}
					with your data. Please
					{{#if this.resolveSourcesData.hasOnlyChoose}}choose{{else}}connect{{/if}}
					the following
					{{this.sourceNoun}}:
				</p>

				<div class="actionList">

					<ul class="sources-map" {{didInsert this.captureScrollContainer}}>
						{{#each this.resolveSourcesData.sourcesToConnect as |sourceToConnect|}}
							<li class="t1">
								<AvatarOrInitialsComponent
									@name={{sourceToConnect.sourceType.name}}
									@avatar={{sourceToConnect.sourceType.avatar}}
								/>
								<span class="sourceTitle">{{sourceToConnect.sourceType.name}}</span>
								<div class="actions">
									<CommonAskToConnectButtonComponent
										@model={{sourceToConnect.sourceType}}
									/>
									<SubmitButtonComponent
										@text="Connect"
										@icon="Connect"
										@noDelay={{true}}
										@onTrigger={{fn this.connect sourceToConnect}}
										@theme="green"
									/>
								</div>
							</li>
						{{/each}}
						{{#each this.resolveSourcesData.sourcesToChoose as |source|}}
							<li class="t2">
								<AvatarOrInitialsComponent
									@name={{source.sourceType.name}}
									@avatar={{source.sourceType.avatar}}
								/>
								<span class="sourceTitle">{{source.sourceType.name}}</span>
								<ResolveDataItemChoiceComponent
									@sourceData={{source}}
									@onChoose={{this.resolveSource}}
									@checkPublicTemplatesSpace={{true}}
									@isUseTemplate={{true}}
								/>
							</li>
						{{/each}}

						{{#each this.mergedDatasetGroups as |group|}}
							<li class="merged-dataset-header">
								<AvatarOrInitialsComponent
									@name="Merged dataset"
									@avatar="https://cdn1.databox.com/images/apps/s/Merged.png"
								/>
								<span class="sourceTitle"><strong>Merged dataset</strong></span>
							</li>
							{{#each group.requiredSources as |source|}}
								<li class="t2 merged-dataset-source">
									<span class="merged-dataset-arrow">↳</span>
									<AvatarOrInitialsComponent
										@name={{source.resolvedName}}
										@avatar={{source.resolvedAvatar}}
									/>
									<span class="sourceTitle">{{source.resolvedName}}</span>
									<ResolveDataItemChoiceComponent
										@sourceData={{source.sourceData}}
										@onChoose={{fn this.resolveMergeSource group source}}
										@checkPublicTemplatesSpace={{true}}
										@isUseTemplate={{true}}
									/>
								</li>
							{{/each}}
						{{/each}}
					</ul>

				</div>

				{{#if this.isSecurityInfoShown}}
					<CommonSecurityInfoComponent />
				{{/if}}

				<div class="controls2">
					<div class="controls2__right">
						<SubmitButtonComponent
							@text="Continue"
							@isDisabled={{this.isContinueButtonDisabled}}
							@onTrigger={{this.finishResolving}}
							@extraClass="continue"
						/>
					</div>
				</div>

			</div>
		{{else if this.currentDatasetGroup}}
			{{! Steps 2+: Dataset selection }}
			<TemplatesPopupResolveDatasetComponent
				@group={{this.currentDatasetGroup}}
				@compatibleDatasets={{this.compatibleDatasets}}
				@isLoading={{this.compatibleDatasetsLoading}}
				@stepIndex={{this.currentDatasetGroupIndex}}
				@totalSteps={{this.totalSteps}}
				@initialSelection={{this.currentDatasetInitialSelection}}
				@onContinue={{this.onDatasetContinue}}
				@onSkip={{this.onDatasetSkip}}
				@onBack={{this.onDatasetBack}}
			/>
		{{/if}}
	</template>
}
